import { useEffect, useState } from 'react';
import { createEvidencePreviewUrl } from '../services/issues';
import {
  completeCloseWithEvidence,
  completeCloseWithWaiver,
  loadClosureEvidence,
  loadClosureEvents,
  reopenAfterRecheck,
  uploadClosureEvidence,
} from '../services/close';
import { isTerminalCloseStatus, validateReopenNote, validateWaiver } from '../lib/close';
import type { ActionRecord } from '../types/actions';
import type { ClosureEventRecord, ClosureMethod, RecheckResult } from '../types/close';
import type { EvidenceRecord, IssueRecord } from '../types/issues';

interface CloseWorkspaceProps {
  ownerId: string;
  issue: IssueRecord;
  action: ActionRecord;
  onBack: () => void;
  onActionUpdated: (action: ActionRecord) => void;
}

type AsyncStatus = 'loading' | 'ready' | 'saving' | 'error';

function formatDateTime(value: string): string {
  return new Intl.DateTimeFormat('en-IN', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
}

export function CloseWorkspace({ ownerId, issue, action, onBack, onActionUpdated }: CloseWorkspaceProps) {
  const [events, setEvents] = useState<ClosureEventRecord[]>([]);
  const [evidence, setEvidence] = useState<EvidenceRecord[]>([]);
  const [selectedEvidenceId, setSelectedEvidenceId] = useState('');
  const [recheckResult, setRecheckResult] = useState<RecheckResult>('rectified');
  const [method, setMethod] = useState<ClosureMethod>('evidence');
  const [closureNote, setClosureNote] = useState('');
  const [reopenNote, setReopenNote] = useState('');
  const [waiverAcknowledged, setWaiverAcknowledged] = useState(false);
  const [waiverReason, setWaiverReason] = useState('');
  const [status, setStatus] = useState<AsyncStatus>('loading');
  const [message, setMessage] = useState<string | null>(null);

  async function refresh() {
    const [nextEvents, nextEvidence] = await Promise.all([
      loadClosureEvents(ownerId, action.id),
      loadClosureEvidence(ownerId, issue.id),
    ]);
    setEvents(nextEvents);
    setEvidence(nextEvidence);
    const latestEvidence = nextEvidence.at(-1);
    if (!selectedEvidenceId && latestEvidence) {
      setSelectedEvidenceId(latestEvidence.id);
    }
  }

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setStatus('loading');
      try {
        const [nextEvents, nextEvidence] = await Promise.all([
          loadClosureEvents(ownerId, action.id),
          loadClosureEvidence(ownerId, issue.id),
        ]);
        if (cancelled) return;
        setEvents(nextEvents);
        setEvidence(nextEvidence);
        const latestEvidence = nextEvidence.at(-1);
        if (latestEvidence) setSelectedEvidenceId(latestEvidence.id);
        setStatus('ready');
      } catch (caught) {
        if (!cancelled) {
          setStatus('error');
          setMessage(caught instanceof Error ? caught.message : 'Could not load Close history.');
        }
      }
    }
    void load();
    return () => { cancelled = true; };
  }, [action.id, issue.id, ownerId]);

  async function handleUpload(file: File | null) {
    if (!file) return;
    setStatus('saving');
    setMessage(null);
    try {
      const uploaded = await uploadClosureEvidence(
        ownerId,
        issue.assessment_id,
        issue.id,
        file,
        closureNote || null,
      );
      setEvidence((current) => [...current, uploaded]);
      setSelectedEvidenceId(uploaded.id);
      setStatus('ready');
      setMessage('Closure photo uploaded privately. Review it before closing.');
    } catch (caught) {
      setStatus('error');
      setMessage(caught instanceof Error ? caught.message : 'Could not upload closure evidence.');
    }
  }

  async function openEvidence(item: EvidenceRecord) {
    try {
      const url = await createEvidencePreviewUrl(item.storage_path);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : 'Could not open private closure evidence.');
    }
  }

  async function handleCloseWithEvidence() {
    if (!selectedEvidenceId) {
      setMessage('Upload or select closure evidence before verified closure.');
      return;
    }
    setStatus('saving');
    setMessage(null);
    try {
      const updated = await completeCloseWithEvidence(
        ownerId,
        issue,
        action,
        selectedEvidenceId,
        closureNote,
      );
      onActionUpdated(updated);
      await refresh();
      setStatus('ready');
      setMessage('Closed with buyer-recorded closure evidence. This is not a technical certification.');
    } catch (caught) {
      setStatus('error');
      setMessage(caught instanceof Error ? caught.message : 'Could not close this action.');
    }
  }

  async function handleCloseWithWaiver() {
    const validationError = validateWaiver(waiverAcknowledged, waiverReason);
    if (validationError) {
      setMessage(validationError);
      return;
    }
    setStatus('saving');
    setMessage(null);
    try {
      const updated = await completeCloseWithWaiver(
        ownerId,
        issue,
        action,
        waiverAcknowledged,
        waiverReason,
        closureNote,
      );
      onActionUpdated(updated);
      await refresh();
      setStatus('ready');
      setMessage('Action closed by explicit buyer waiver without closure evidence.');
    } catch (caught) {
      setStatus('error');
      setMessage(caught instanceof Error ? caught.message : 'Could not save the waiver closure.');
    }
  }

  async function handleReopen() {
    const validationError = validateReopenNote(reopenNote);
    if (validationError) {
      setMessage(validationError);
      return;
    }
    setStatus('saving');
    setMessage(null);
    try {
      const updated = await reopenAfterRecheck(ownerId, issue, action, reopenNote);
      onActionUpdated(updated);
      setStatus('ready');
      setMessage('Recheck failed. Action reopened and returned to in progress.');
    } catch (caught) {
      setStatus('error');
      setMessage(caught instanceof Error ? caught.message : 'Could not reopen this action.');
    }
  }

  const terminal = isTerminalCloseStatus(action.status);

  return (
    <main className="page close-page">
      <button className="text-button" type="button" onClick={onBack}>← Back to action</button>

      <section className="close-hero">
        <span className="eyebrow">Close</span>
        <h1>Recheck before you close the finding</h1>
        <p className="lead">
          A builder saying “fixed” is not closure. Record your recheck, preserve evidence where possible, or explicitly acknowledge a waiver.
        </p>
      </section>

      <section className="close-context-card">
        <span className="eyebrow">Finding being rechecked</span>
        <h2>{issue.title}</h2>
        <p>{issue.description || 'No additional note recorded.'}</p>
        <div className="issue-meta"><span>{issue.location}</span><span>{action.status.replaceAll('_', ' ')}</span></div>
      </section>

      {message ? <div className="status-banner" role="status">{message}</div> : null}
      {status === 'loading' ? <div className="status-banner">Loading Close history…</div> : null}

      {terminal ? (
        <section className="close-card close-complete-card">
          <span className="eyebrow">Closure recorded</span>
          <h2>{action.status === 'verified_closed' ? 'Closed with evidence' : 'Closed by buyer waiver'}</h2>
          <p>
            This records the buyer workflow only. It is not a structural, electrical, plumbing, legal or regulatory certification.
          </p>
        </section>
      ) : action.status !== 'rectification_claimed' ? (
        <section className="close-card">
          <h2>Close is not available yet</h2>
          <p>The accountable party must first claim rectification before this buyer recheck workflow can start.</p>
        </section>
      ) : (
        <>
          <section className="close-card">
            <span className="eyebrow">1. Recheck result</span>
            <h2>What did you observe now?</h2>
            <div className="close-choice-grid">
              <label className={`close-choice ${recheckResult === 'rectified' ? 'close-choice-active' : ''}`}>
                <input type="radio" name="recheck" checked={recheckResult === 'rectified'} onChange={() => setRecheckResult('rectified')} />
                <strong>Looks rectified on recheck</strong>
                <span>Continue to evidence or explicit waiver.</span>
              </label>
              <label className={`close-choice ${recheckResult === 'still_needs_correction' ? 'close-choice-active' : ''}`}>
                <input type="radio" name="recheck" checked={recheckResult === 'still_needs_correction'} onChange={() => setRecheckResult('still_needs_correction')} />
                <strong>Still needs correction</strong>
                <span>Reopen the action and preserve the previous history.</span>
              </label>
            </div>
          </section>

          {recheckResult === 'still_needs_correction' ? (
            <section className="close-card">
              <span className="eyebrow">Reopen</span>
              <h2>Record what remains unresolved</h2>
              <textarea rows={4} value={reopenNote} onChange={(event) => setReopenNote(event.target.value)} placeholder="Example: Damp patch is still visible after the builder rework." />
              <button className="button button-primary" type="button" disabled={status === 'saving'} onClick={() => void handleReopen()}>
                Reopen action
              </button>
            </section>
          ) : (
            <>
              <section className="close-card">
                <span className="eyebrow">2. Closure basis</span>
                <h2>How do you want to record closure?</h2>
                <div className="close-tab-row">
                  <button className={`filter-chip ${method === 'evidence' ? 'filter-chip-active' : ''}`} type="button" onClick={() => setMethod('evidence')}>Closure evidence</button>
                  <button className={`filter-chip ${method === 'waiver' ? 'filter-chip-active' : ''}`} type="button" onClick={() => setMethod('waiver')}>Explicit waiver</button>
                </div>
                <label className="field">
                  <span>Recheck / closure note</span>
                  <textarea rows={3} value={closureNote} onChange={(event) => setClosureNote(event.target.value)} placeholder="What did you observe during recheck?" />
                </label>
              </section>

              {method === 'evidence' ? (
                <section className="close-card">
                  <span className="eyebrow">3. Evidence</span>
                  <h2>Add a current recheck photo</h2>
                  <p>Use a photo that shows the area after rectification. NivasaSaathi stores it privately and does not diagnose the photo.</p>
                  <label className="button button-secondary ink-button close-upload-button">
                    Add closure photo
                    <input type="file" accept="image/*" capture="environment" hidden onChange={(event) => void handleUpload(event.target.files?.[0] ?? null)} />
                  </label>

                  {evidence.length > 0 ? (
                    <div className="close-evidence-list">
                      {evidence.map((item) => (
                        <label className="close-evidence-row" key={item.id}>
                          <input type="radio" name="closure-evidence" checked={selectedEvidenceId === item.id} onChange={() => setSelectedEvidenceId(item.id)} />
                          <span><strong>{item.file_name}</strong><small>{formatDateTime(item.created_at)}</small></span>
                          <button className="text-button" type="button" onClick={(event) => { event.preventDefault(); void openEvidence(item); }}>View</button>
                        </label>
                      ))}
                    </div>
                  ) : <p className="inline-note">No closure photo uploaded yet.</p>}

                  <button className="button button-primary" type="button" disabled={!selectedEvidenceId || status === 'saving'} onClick={() => void handleCloseWithEvidence()}>
                    Close with evidence
                  </button>
                </section>
              ) : (
                <section className="close-card close-waiver-card">
                  <span className="eyebrow">3. Waiver</span>
                  <h2>Close without closure evidence</h2>
                  <p>This path is intentionally explicit. Use it only when you choose to close without a closure photo.</p>
                  <label className="checkbox-row">
                    <input type="checkbox" checked={waiverAcknowledged} onChange={(event) => setWaiverAcknowledged(event.target.checked)} />
                    <span>I understand that this closes the action without closure evidence and is not verified closure.</span>
                  </label>
                  <label className="field">
                    <span>Reason for waiver, minimum 20 characters *</span>
                    <textarea rows={4} value={waiverReason} onChange={(event) => setWaiverReason(event.target.value)} placeholder="Explain why closure evidence is unavailable or being waived." />
                  </label>
                  <button className="button button-primary" type="button" disabled={status === 'saving'} onClick={() => void handleCloseWithWaiver()}>
                    Close with explicit waiver
                  </button>
                </section>
              )}
            </>
          )}
        </>
      )}

      {events.length > 0 ? (
        <section className="close-card">
          <span className="eyebrow">Closure history</span>
          <div className="timeline-list">
            {events.map((event) => (
              <article className="timeline-item" key={event.id}>
                <div><strong>{event.waiver_acknowledged ? 'Waiver closure' : 'Evidence closure'}</strong><span>{formatDateTime(event.created_at)}</span></div>
                {event.closure_note ? <p>{event.closure_note}</p> : null}
                {event.waiver_reason ? <p>Waiver reason: {event.waiver_reason}</p> : null}
              </article>
            ))}
          </div>
        </section>
      ) : null}
    </main>
  );
}
